import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const TRACKS = ["discover", "vault", "promoted"];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [trackFilter, setTrackFilter] = useState("all");
  const [restockFor, setRestockFor] = useState(null);

  const load = () => adminApi.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await adminApi.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const moveTrack = async (id, track) => {
    try {
      await adminApi.put(`/admin/products/${id}/track`, { track });
      toast.success(`Moved to ${track.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
    }
  };

  const visible = useMemo(() => (
    trackFilter === "all" ? products : products.filter((p) => p.track === trackFilter)
  ), [products, trackFilter]);

  return (
    <AdminShell>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="brand-tag">// Inventory</div>
          <h1 className="font-display text-5xl uppercase tracking-tight mt-1">Products</h1>
        </div>
        <Button
          onClick={() => { setEdit(null); setOpen(true); }}
          className="rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-11 px-6"
          data-testid="new-product-btn"
        >
          + New Product
        </Button>
      </div>

      {/* Track filter pills */}
      <div className="mb-4 inline-flex border border-[#E5E1D8] bg-[#FFFFFF] p-1" data-testid="product-track-filter">
        {["all", ...TRACKS].map((t) => (
          <button
            key={t}
            onClick={() => setTrackFilter(t)}
            data-testid={`track-filter-${t}`}
            className={`px-4 h-9 font-mono-ds text-[11px] uppercase tracking-widest transition-colors ${
              trackFilter === t ? "bg-[#FF6A00] text-[#161616] font-bold" : "text-[#6E6E6E] hover:text-[#161616]"
            }`}
          >
            {t}
            <span className="ml-2 text-[10px] opacity-60">
              {t === "all" ? products.length : products.filter((p) => p.track === t).length}
            </span>
          </button>
        ))}
      </div>

      <div className="border border-[#E5E1D8] bg-[#FFFFFF] overflow-x-auto">
        <table className="w-full text-sm font-mono-ds min-w-[920px]">
          <thead className="border-b border-[#E5E1D8] text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
            <tr>
              <th className="text-left p-4">Product</th>
              <th className="text-left p-4">Category</th>
              <th className="text-left p-4">Track</th>
              <th className="text-left p-4">Price</th>
              <th className="text-left p-4">Stock</th>
              <th className="text-left p-4">Votes</th>
              <th className="text-left p-4">Requests</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#6E6E6E]">// No products in this view</td></tr>
            )}
            {visible.map((p) => (
              <tr key={p.product_id} className="border-b border-[#FFFFFF] hover:bg-[#FFFFFF]/70" data-testid={`product-row-${p.product_id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 object-cover grayscale" />}
                    <div>
                      <div className="text-[#161616]">{p.name}</div>
                      <div className="text-[10px] text-[#6E6E6E] truncate max-w-xs">{p.description}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-[#6E6E6E]">{p.category || "—"}</td>
                <td className="p-4">
                  <select
                    value={p.track || "discover"}
                    onChange={(e) => moveTrack(p.product_id, e.target.value)}
                    data-testid={`track-select-${p.product_id}`}
                    className={`bg-transparent border px-2 py-1 font-mono-ds text-[10px] uppercase tracking-widest focus:border-[#FF6A00] outline-none ${
                      p.track === "vault" ? "border-[#FF6A00] text-[#FF6A00]" :
                      p.track === "promoted" ? "border-[#C4BEB0] text-[#6E6E6E]" :
                      "border-[#E5E1D8] text-[#6E6E6E]"
                    }`}
                  >
                    {TRACKS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
                <td className="p-4 text-[#161616]">₹{p.base_price}</td>
                <td className="p-4">
                  <span className={(p.stock ?? 0) <= 0 ? "text-[#FF6A00] font-bold" : (p.stock ?? 0) <= 10 ? "text-[#FF6A00]" : "text-[#161616]"}>
                    {p.stock ?? 0}
                  </span>
                </td>
                <td className="p-4 text-[#6E6E6E]">{(p.votes ?? 0).toLocaleString()}</td>
                <td className="p-4 text-[#6E6E6E]">{p.request_count ?? 0}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button
                    className="text-[#6E6E6E] hover:text-[#FF6A00] uppercase text-xs tracking-widest mr-3"
                    onClick={() => setRestockFor(p)}
                    data-testid={`restock-${p.product_id}`}
                  >Restock</button>
                  <button
                    className="text-[#6E6E6E] hover:text-[#FF6A00] uppercase text-xs tracking-widest mr-3"
                    onClick={() => { setEdit(p); setOpen(true); }}
                    data-testid={`edit-product-${p.product_id}`}
                  >Edit</button>
                  <button
                    className="text-[#6E6E6E] hover:text-[#FF6A00] uppercase text-xs tracking-widest"
                    onClick={() => remove(p.product_id)}
                    data-testid={`delete-product-${p.product_id}`}
                  >Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProductDialog open={open} setOpen={setOpen} edit={edit} onSaved={load} />
      <RestockDialog product={restockFor} onClose={() => setRestockFor(null)} onSaved={load} />
    </AdminShell>
  );
}

function ProductDialog({ open, setOpen, edit, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (edit) setForm({
      name: edit.name, description: edit.description || "",
      image_url: edit.image_url || "", base_price: edit.base_price,
      mrp: edit.mrp || 0, starting_price: edit.starting_price || 0, final_price: edit.final_price || 0,
      stock: edit.stock || 0, category: edit.category || "General",
      track: edit.track || "discover",
    });
    else setForm(emptyForm());
  }, [edit, open]);

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 800 * 1024) {
      toast.error("Image too large. Keep it under 800KB.");
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, image_url: reader.result }));
      setUploading(false);
    };
    reader.onerror = () => { toast.error("Could not read file"); setUploading(false); };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        base_price: Number(form.base_price) || Number(form.starting_price) || 0,
      };
      if (edit) await adminApi.put(`/products/${edit.product_id}`, payload);
      else await adminApi.post("/products", payload);
      toast.success(edit ? "Updated" : "Created");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FFFFFF] border border-[#E5E1D8] rounded-2xl max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">{edit ? "Edit Product" : "New Product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 font-mono-ds text-sm" data-testid="product-form">
          <Field label="Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
              data-testid="product-name-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="Apparel, Bags, EDC…"
                className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
                data-testid="product-category-input" />
            </Field>
            <Field label="Track">
              <select value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value })}
                className="w-full bg-[#FFFFFF] border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
                data-testid="product-track-select">
                {TRACKS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2} className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none" />
          </Field>

          {/* Image: file upload OR URL */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">Product Image</label>
            <div className="mt-1 grid grid-cols-[88px_1fr] gap-3">
              <div className="border border-[#E5E1D8] bg-[#FFFFFF] aspect-square flex items-center justify-center overflow-hidden">
                {form.image_url ? (
                  <img src={form.image_url} alt="" className="w-full h-full object-cover" data-testid="product-image-preview" />
                ) : (
                  <span className="font-mono-ds text-[9px] uppercase tracking-widest text-[#6E6E6E]">No image</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="border border-dashed border-[#E5E1D8] hover:border-[#FF6A00] hover:text-[#FF6A00] cursor-pointer px-3 py-2 font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] flex items-center justify-center gap-2">
                  {uploading ? "Uploading…" : "↑ Upload image (≤800KB)"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onFile(e.target.files?.[0])}
                    data-testid="product-image-file"
                  />
                </label>
                <input
                  value={form.image_url?.startsWith("data:") ? "" : form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="…or paste an image URL"
                  className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none text-xs"
                  data-testid="product-image-url-input"
                />
                {form.image_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_url: "" })}
                    className="self-start text-[10px] uppercase tracking-widest text-[#6E6E6E] hover:text-[#FF6A00]"
                  >
                    Remove image
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Pricing trio */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">Pricing</label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              <Subfield label="MRP">
                <input required type="number" min="0" step="0.01" value={form.mrp}
                  onChange={(e) => setForm({ ...form, mrp: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-transparent border border-[#E5E1D8] px-2 py-2 focus:border-[#FF6A00] outline-none"
                  data-testid="product-mrp-input" />
              </Subfield>
              <Subfield label="Starting">
                <input required type="number" min="0" step="0.01" value={form.starting_price}
                  onChange={(e) => setForm({ ...form, starting_price: parseFloat(e.target.value) || 0, base_price: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-transparent border border-[#E5E1D8] px-2 py-2 focus:border-[#FF6A00] outline-none"
                  data-testid="product-starting-input" />
              </Subfield>
              <Subfield label="Final">
                <input required type="number" min="0" step="0.01" value={form.final_price}
                  onChange={(e) => setForm({ ...form, final_price: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-transparent border border-[#E5E1D8] px-2 py-2 focus:border-[#FF6A00] outline-none"
                  data-testid="product-final-input" />
              </Subfield>
            </div>
            <div className="mt-1 text-[10px] text-[#6E6E6E]">
              MRP = shown strikethrough · Starting = day-1 live price · Final = lowest milestone price
            </div>
          </div>

          <Field label="Stock">
            <input required type="number" min="0" value={form.stock}
              onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value, 10) || 0 })}
              className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
              data-testid="product-stock-input" />
          </Field>

          <Button type="submit" className="w-full rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-11" data-testid="save-product-btn">
            Save
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RestockDialog({ product, onClose, onSaved }) {
  const [amount, setAmount] = useState(10);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await adminApi.post(`/admin/products/${product.product_id}/restock`, { amount: Number(amount) });
      toast.success(`Restocked. New stock: ${r.data.stock}`);
      onClose();
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restock failed");
    }
  };
  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border border-[#E5E1D8] rounded-2xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase tracking-tight">Restock</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 font-mono-ds text-sm" data-testid="restock-form">
          <div className="text-[#6E6E6E]">
            <span className="text-[#161616]">{product?.name}</span>
            <div className="text-[10px] text-[#6E6E6E] mt-1">Current: {product?.stock ?? 0}</div>
          </div>
          <Field label="Add units">
            <input required type="number" min="-9999" value={amount}
              onChange={(e) => setAmount(parseInt(e.target.value, 10) || 0)}
              className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
              data-testid="restock-amount-input" />
            <div className="text-[10px] text-[#6E6E6E] mt-1">Use negative numbers to remove units.</div>
          </Field>
          <Button type="submit" className="w-full rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-11" data-testid="save-restock-btn">
            Apply
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function emptyForm() {
  return {
    name: "", description: "", image_url: "",
    base_price: 0, mrp: 0, starting_price: 0, final_price: 0,
    stock: 0, category: "General", track: "discover",
  };
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Subfield({ label, children }) {
  return (
    <div>
      <label className="font-mono-ds text-[9px] uppercase tracking-[0.3em] text-[#FF6A00]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
